import { useEffect, useState } from "react";
import { honeyStockApi } from "../../services/businessApi";
import { Button } from "../common/Button";
import { PutAwayDisclosure, splitPutAway } from "../common/PutAwaySection";
import { Badge } from "../common/Badge";
import { DataTable } from "../common/DataTable";
import { PromptDialog } from "../common/PromptDialog";
import { useToast } from "../../context/ToastContext";

const MUTED = { color: "var(--color-text-muted)" };

/**
 * Tidy the varietal list (#358, #410).
 *
 * Reported from a device: a varietal named "5" -- the quantity typed into
 * the name field -- became a permanent option on every picker in both
 * apps, with no way to remove it. The pickers could ADD and nothing could
 * take away, so the only list in the app that grows by accident was also
 * the only one that could never shrink.
 *
 * "PUT AWAY", NOT "DELETE", and the wording is load-bearing rather than
 * squeamish. Honey in the pool is keyed on the varietal's id: the row has
 * to survive, or last season's Clover renders as a blank. So the copy says
 * what actually happens -- it stops being offered for new honey -- because
 * a button labelled Delete that leaves the data in place teaches people the
 * app is lying somewhere, and they are then right to distrust the rest of
 * it. Same reasoning as the jar-size control, which says "put away" rather
 * than "deleted" for the same reason.
 *
 * RENAME IS OFFERED HERE AND NOT ON THE GENERIC OPTION LISTS, for a real
 * reason rather than an oversight: everything downstream of a varietal
 * joins on its id and reads the name live, so a rename propagates through
 * history at once. The generic lists store the TEXT on the consuming row,
 * where a rename would leave old records under the old spelling and new
 * ones under the new. Same gesture, opposite consequence.
 *
 * ARCHIVED BUT STILL STOCKED STAYS VISIBLE. That is the question #358 left
 * open, and hiding it is the answer that strands honey: jars whose varietal
 * cannot be chosen cannot be bottled, sold or moved. The row stays, marked,
 * until its balance reaches zero -- and VarietalPicker keeps offering it on
 * the same rule.
 *
 * ── A PANEL NOW, NOT A CARD OF ITS OWN (#410) ──
 *
 *     "the Your Varietals dropdown is good but I think it should be
 *      hidden and put a 'Manage' button to the right of the 'By
 *      Varietal' label"
 *
 * It used to sit permanently below the breakdown, which put the varietal
 * list on the page TWICE -- once as the numbers people came for, once as
 * a table of the same names with buttons. Curating the list is the
 * rarest gesture on this page: you do it once, after a typo. So the card
 * above hosts it, and this takes the breakdown's place when asked for.
 *
 * WHICH IS WHY `adding` ARRIVES FROM OUTSIDE. The Add button belongs in
 * the card header next to the label, where Bottle sizes already puts it,
 * and the card cannot reach into this component's state -- so the card
 * owns the boolean and this owns what to do about it.
 */
export function VarietalManager({ adding, onAddingClose, onChanged, onClose }) {
  const { notify } = useToast();
  const [varietals, setVarietals] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await honeyStockApi.listVarietals();
      setVarietals(r.varietals || []);
    } catch {
      // Best-effort, like VarietalBreakdown around it: a list that fails
      // to load must not take down the pool page it sits on.
      setVarietals([]);
    }
  }

  useEffect(() => { load(); }, []);

  async function change(varietal, changes, said) {
    setBusy(true);
    try {
      await honeyStockApi.updateVarietal(varietal.varietal_id, changes);
      notify(said, "success");
      await load();
      // The pickers on this page are holding the old list. Without this a
      // varietal just put away stays selectable until something else
      // refreshes.
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not change that varietal", "danger");
    } finally {
      setBusy(false);
      setRenaming(null);
    }
  }

  async function add(name) {
    setBusy(true);
    try {
      await honeyStockApi.createVarietal(name);
      notify(`"${name}" added.`, "success");
      await load();
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not add that varietal", "danger");
    } finally {
      setBusy(false);
      onAddingClose?.();
    }
  }

  /*
   * General is the account's fallback: every harvest and bottling that
   * named no varietal is recorded as it. The server refuses to rename or
   * archive it -- this hides the controls so nobody reaches a refusal they
   * could not have predicted, and says why rather than leaving a blank
   * where two buttons sit on every other row.
   */
  const isGeneral = (v) => v.name?.trim().toLowerCase() === "general";

  /* `!v.is_active` is how this file already reads the flag, so the test
     passed to splitPutAway matches it rather than guessing. */
  const { active: activeVarietals, putAway: putAwayVarietals } =
    splitPutAway(varietals ?? [], (v) => Boolean(v.is_active));
  const [showPutAway, setShowPutAway] = useState(false);

  return (
    <>
      <p style={{ marginTop: 0, ...MUTED }}>
        Renaming one updates it everywhere, including honey you recorded last
        season. Putting one away stops it being offered for new honey and
        changes nothing you have already recorded.
      </p>
      {/*
        * PUT AWAY GOES TO THE BOTTOM, BEHIND A DISCLOSURE (#486).
        *
        * Appended to the same table rather than rendered as a second one,
        * so the columns stay defined once. The rows keep their "Put away"
        * badge, which is what tells you which half you are looking at
        * once the list is expanded.
        */}
      <DataTable
        rows={varietals === null
          ? varietals
          : (showPutAway ? [...activeVarietals, ...putAwayVarietals] : activeVarietals)}
        loading={varietals === null}
        rowKey="varietal_id"
        emptyTitle="No varietals yet"
        emptyDescription="Add the kinds of honey you keep separate — clover, wildflower, whatever you sell by name."
        columns={[
          {
            key: "name",
            header: "Varietal",
            render: (v) => (
              <>
                {v.name}
                {!v.is_active && <> <Badge tone="neutral">Put away</Badge></>}
                {isGeneral(v) && (
                  <span style={{ ...MUTED, fontSize: "var(--font-size-sm)", display: "block" }}>
                    Used when no varietal is chosen
                  </span>
                )}
              </>
            ),
          },
          {
            key: "pounds_on_hand",
            header: "In the pool",
            render: (v) => (Number(v.pounds_on_hand) > 0
              ? `${Number(v.pounds_on_hand).toFixed(1)} lb`
              : <span style={MUTED}>—</span>),
          },
          {
            key: "actions",
            header: "",
            render: (v) => (isGeneral(v) ? null : (
              <>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => setRenaming(v)}>
                  Rename
                </Button>
                {" "}
                {v.is_active ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => change(
                      v,
                      { is_active: false },
                      `"${v.name}" put away. Honey already recorded as ${v.name} is unchanged.`,
                    )}
                  >
                    Put away
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => change(v, { is_active: true }, `"${v.name}" is back on your list.`)}
                  >
                    Bring back
                  </Button>
                )}
              </>
            )),
          },
        ]}
      />

      {/* At the BOTTOM, which is where you are once you have finished
          reading the list. A Done in the header would be a second control
          beside Add, competing for the same corner. */}
      <Button variant="secondary" onClick={onClose} style={{ marginTop: "var(--space-3)" }}>
        Done
      </Button>

      <PromptDialog
        open={renaming !== null}
        onClose={() => setRenaming(null)}
        onSubmit={(name) => (name
          ? change(renaming, { name }, `Renamed to ${name}.`)
          : setRenaming(null))}
        title="Rename this honey"
        label={renaming ? `What should "${renaming.name}" be called?` : ""}
        // Opens holding the current name, so fixing "Clovr" is one
        // keystroke rather than retyping the word.
        initialValue={renaming ? renaming.name : ""}
        hint="This updates the name everywhere, including honey you have already recorded."
        confirmLabel="Rename"
        busy={busy}
        // 100, matching VarietalUpdateRequest. A prompt that accepts more
        // than the API does refuses the name only after the user presses
        // Rename.
        maxLength={100}
      />

      <PromptDialog
        open={Boolean(adding)}
        onClose={() => onAddingClose?.()}
        onSubmit={(name) => (name ? add(name) : onAddingClose?.())}
        title="Add a varietal"
        label="What is this honey called?"
        hint="Clover, wildflower, whatever you sell it as. You can rename it later."
        confirmLabel="Add"
        busy={busy}
        maxLength={100}
      />
      <PutAwayDisclosure
        count={putAwayVarietals.length}
        open={showPutAway}
        onToggle={() => setShowPutAway((v) => !v)}
      />
    </>
  );
}
